import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { linkedListProblems } from '../../data/practice/linkedListProblems';

const LinkedListPractice: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Get unique tags from all problems
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    linkedListProblems.forEach(problem => {
      problem.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags);
  }, []);

  // Filter problems based on search term, difficulty, and tags
  const filteredProblems = useMemo(() => {
    return linkedListProblems.filter(problem => {
      const matchesSearch = problem.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          problem.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDifficulty = selectedDifficulty === 'all' || problem.difficulty === selectedDifficulty;
      const matchesTags = selectedTags.length === 0 || 
                         selectedTags.every(tag => problem.tags.includes(tag));
      
      return matchesSearch && matchesDifficulty && matchesTags;
    });
  }, [searchTerm, selectedDifficulty, selectedTags]);

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Minimal grid background */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:64px_64px] [background-position-x:center]"></div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-8">
          <motion.button
            onClick={() => navigate('/topics')}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Back to Topics</span>
          </motion.button>
        </div>

        {/* Title and description */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-normal text-white mb-2">Linked List Practice</h1>
          <p className="text-white/70 text-lg">Master linked list operations with these carefully selected problems.</p>
        </motion.div>

        {/* Filters */}
        <div className="mb-8 space-y-4">
          {/* Search bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search problems..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
            />
            <svg className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Difficulty filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedDifficulty('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedDifficulty === 'all'
                  ? 'bg-white/20 text-white'
                  : 'bg-white/5 text-white/70 hover:bg-white/10'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedDifficulty('Easy')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedDifficulty === 'Easy'
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-white/5 text-white/70 hover:bg-white/10'
              }`}
            >
              Easy
            </button>
            <button
              onClick={() => setSelectedDifficulty('Medium')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedDifficulty === 'Medium'
                  ? 'bg-yellow-500/20 text-yellow-400'
                  : 'bg-white/5 text-white/70 hover:bg-white/10'
              }`}
            >
              Medium
            </button>
            <button
              onClick={() => setSelectedDifficulty('Hard')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedDifficulty === 'Hard'
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-white/5 text-white/70 hover:bg-white/10'
              }`}
            >
              Hard
            </button>
          </div>

          {/* Tags filter */}
          <div className="flex flex-wrap gap-2">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => handleTagToggle(tag)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  selectedTags.includes(tag)
                    ? 'bg-white/20 text-white'
                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Problems list */}
        <div className="space-y-4">
          {filteredProblems.map((problem, index) => (
            <motion.div
              key={problem.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white/[0.02] border border-white/10 rounded-xl p-6 hover:bg-white/[0.04] transition-colors cursor-pointer"
              onClick={() => navigate(`/practice/linked-list/${problem.id}`)}
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">{problem.title}</h3>
                <span className={`px-3 py-1 rounded-full text-sm ${
                  problem.difficulty === 'Easy' ? 'bg-green-500/20 text-green-400' :
                  problem.difficulty === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {problem.difficulty}
                </span>
              </div>
              <p className="text-white/70 mb-4">{problem.description.split('\n\n')[0]}</p>
              <div className="flex flex-wrap gap-2">
                {problem.tags.map(tag => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-white/5 rounded-full text-sm text-white/50"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LinkedListPractice; 